import { FeatureKeys, ModularProductTier, UserProfiles } from '@apis/common';
// eslint-disable-next-line import/no-cycle
import ProtectedRoute from '@components/molecules/protected-route';
import { MixpanelEvents } from '@hooks/useMixpanel';
import { Icon, IconName } from '@minds/minds-react-sdk';
import useTrialGuard from '@pages/main/trial/useGuard';
import React, { ComponentProps, ReactNode } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import {
    CASES_PATH,
    COMPANY_LOGIN_PATH,
    CONFIG_PATH,
    CUSTOMERS_PATH,
    DASHBOARD_PATH,
    FLAGS_FINISH_ANALYSIS_PATH,
    FORGOT_COMPANY_NAME_PATH,
    INSERT_PATH,
    INVITE_PATH,
    LOGIN_PATH,
    NOT_FOUND_PATH,
    SEARCH_CLIENT_PATH,
    SEARCH_PATH,
    SEARCH_PHONE_PATH,
} from './constants';


export interface BaseRouteDefinition {
    icon?: IconName;
    iconProps?: ComponentProps<typeof Icon>;
    path: string;
    alternativePath?: string;
    element?: ReactNode;
    showOnSidebar?: boolean;
    indexRoute?: ReactNode;
    /**
     * @deprecated TODO: remove profilePermission
     */
    profilePermission?: UserProfiles[];
    featurePermission?: FeatureKeys[];
    modularProductPermission?: ModularProductTier[];
    mixpanelName?: MixpanelEvents;
    useGuard?: () => readonly [boolean, boolean];
    externalHref?: string;
    openInNewTab?: boolean;
}

export type RouteDefinition =
    | BaseRouteDefinition
    | { [key: string]: RouteDefinition };

const NotFoundPage = React.lazy(() => import('@pages/public/not-found'));

const LoginPage = React.lazy(() => import('@pages/public/login'));
const OauthLoginPage = React.lazy(() => import('@pages/public/oauth-login'));
const CompanyLoginPage = React.lazy(
    () => import('@pages/public/company-login'),
);
const ForgotCompanyNamePage = React.lazy(
    () => import('@pages/public/forgot-company-name'),
);
const ForgotPasswordPage = React.lazy(
    () => import('@pages/public/forgot-password'),
);
const ResetPasswordPage = React.lazy(
    () => import('@pages/account/reset-password'),
);

const ConfirmAccountPage = React.lazy(
    () => import('@pages/account/confirm-account'),
);
const EditProfilePage = React.lazy(() => import('@pages/account/edit-profile'));

const TrialPage = React.lazy(() => import('@pages/main/trial'));

const CustomersPage = React.lazy(() => import('@pages/main/customers'));

const DashboardPage = React.lazy(() => import('@pages/main/dashboard'));
const FlagsPage = React.lazy(() => import('@pages/main/flags'));
const FlagDetailsPage = React.lazy(
    () => import('@pages/main/flags/flag-details'),
);
const FlagFinishAnalysisPage = React.lazy(
    () => import('@pages/main/flags/finish-analysis/v1'),
);

const SearchClientPage = React.lazy(
    () => import('@pages/main/search/client-search'),
);
const PhoneSearchPage = React.lazy(
    () => import('@pages/main/search/phone-search'),
);
const OperationSearchPage = React.lazy(
    () => import('@pages/main/search/operation-search'),
);

const SilencedListPage = React.lazy(() => import('@pages/main/silenced-list'));
const SilencedListInsertPage = React.lazy(
    () => import('@pages/main/silenced-list/insert'),
);
const SilencedListImportPage = React.lazy(
    () => import('@pages/main/silenced-list/import'),
);

const OperationalReportPage = React.lazy(
    () => import('@pages/reports/operational-report'),
);
const OperationalReportDetailsPage = React.lazy(
    () =>
        import('@pages/reports/operational-report/operational-report-details'),
);
const CertifiedAuthenticationsReportPage = React.lazy(
    () => import('@pages/reports/certified-authentications-report'),
);

const CompanyWebhooksPage = React.lazy(
    () => import('@pages/config/company-webhooks'),
);
const CompanyWebhooksDetailsPage = React.lazy(
    () => import('@pages/config/company-webhooks/company-webhook-details'),
);

const ApiTokensPage = React.lazy(() => import('@pages/config/api-auth'));

const InviteUsersNewPage = React.lazy(
    () => import('@pages/config/users/invite-users'),
);

const ManageUsersNewPage = React.lazy(
    () => import('@pages/config/users/manage-user'),
);

const UsersDetailsPage = React.lazy(
    () => import('@pages/config/users/users-details/users-details'),
);

const LogsPage = React.lazy(() => import('@pages/config/logs-page'));

const GeneralPage = React.lazy(() => import('@pages/config/general'));

const ManageProfilesPage = React.lazy(
    () => import('@pages/config/users/manage-profiles'),
);

const DebugLogsPage = React.lazy(() => import('@pages/config/debug-genesys'));

const EditCreateProfileNewPage = React.lazy(
    // eslint-disable-next-line import/no-cycle
    () => import('@pages/config/users/manage-profiles/edit-create-profile'),
);

const ManageTemplatesPage = React.lazy(
    () => import('@pages/config/manage-templates'),
);
const EditCreateTemplatePage = React.lazy(
    () => import('@pages/config/manage-templates/edit-create-template'),
);

const WatchlistsPage = React.lazy(() => import('@pages/main/watchlists'));
const WatchlistsDetailsPage = React.lazy(
    () => import('@pages/main/watchlists/watchlists-details'),
);
const WatchlistImportPhonesPage = React.lazy(
    () => import('@pages/main/watchlists/watchlists-phone/import'),
);

const FlagsGroupedByPhonesPage = React.lazy(
    () => import('@pages/main/flags/grouped-flags/grouped-by-phones'),
);
const FlagsGroupedByVoicesPage = React.lazy(
    () => import('@pages/main/flags/grouped-flags/grouped-by-voices'),
);

const SuccessfulOperationsReportPage = React.lazy(
    () => import('@pages/reports/operations-report'),
);

const CertifiedBiometricsReportPage = React.lazy(
    () => import('@pages/reports/certified-biometrics-report'),
);
const CertifiedBiometricsReportDetailsPage = React.lazy(
    () =>
        import(
            '@pages/reports/certified-biometrics-report/certified-biometrics-report-details'
        ),
);

const CertifiedBiometricsImportInsertionPage = React.lazy(
    () => import('@components/molecules/import-certified-biometrics/insertion'),
);
const CertifiedBiometricsImportRemovalPage = React.lazy(
    () => import('@components/molecules/import-certified-biometrics/removal'),
);

const ReportFlagsPage = React.lazy(() => import('@pages/reports/report-flags'));
const WatchlistValidationReportPage = React.lazy(
    () => import('@pages/reports/watchlist-validation-report'),
);
const AllowlistValidationReportPage = React.lazy(
    () => import('@pages/reports/allowlist-validation-report'),
);

const ApiDocsPage = React.lazy(() => import('@pages/config/api-docs/api-docs'));

const CredibilityReportPage = React.lazy(
    () => import('@pages/reports/credibility-report'),
);
const PhoneReportPage = React.lazy(() => import('@pages/reports/phone-report'));
const PhoneReportPhonesListPage = React.lazy(
    () => import('@pages/reports/phone-report/phones-list'),
);
const CasesPage = React.lazy(() => import('@pages/main/cases/cases'));
const PhoneCasePage = React.lazy(
    () => import('@pages/main/cases/phone/phone'),
);
const VoiceCasePage = React.lazy(
    () => import('@pages/main/cases/voice/voice'),
);
const GroupedByExternalIdPage = React.lazy(
    () => import('@pages/main/search/grouped-by-external-id'),
);

export const PROTECTED_ROUTES = {
    TRIAL: {
        icon: 'i-ph-device-mobile-speaker-bold',
        iconProps: {
            rotate: 180,
        } as BaseRouteDefinition['iconProps'],
        path: '/trial',
        element: <TrialPage />,
        showOnSidebar: true,
        mixpanelName: MixpanelEvents.ViewTrial,
        featurePermission: [FeatureKeys.Trial],
        useGuard: useTrialGuard,
    },
    DASHBOARD: {
        icon: 'i-ph-chart-bar-bold',
        path: DASHBOARD_PATH,
        element: <DashboardPage />,
        showOnSidebar: true,
        mixpanelName: MixpanelEvents.ViewDashboard,
        featurePermission: [FeatureKeys.Dashboard],
    },
    SEARCH: {
        icon: 'i-mds-user-waves-bold',
        path: SEARCH_PATH,
        indexRoute: <OperationSearchPage />,
        showOnSidebar: true,
        mixpanelName: MixpanelEvents.ViewOperationsSearch,
        featurePermission: [FeatureKeys.Search],
        CLIENT_SEARCH: {
            path: SEARCH_CLIENT_PATH + '/:cpf',
            indexRoute: <SearchClientPage />,
            mixpanelName: MixpanelEvents.ViewClientSearch,
            HISTORY: {
                path: 'history',
                element: <SearchClientPage />,
                mixpanelName: MixpanelEvents.ViewClientSearchHistory,
            },
            VOICE_CLUSTER: {
                path: 'voice-cluster',
                element: <SearchClientPage />,
                featurePermission: [FeatureKeys.SearchVoiceMap],
            },
            FRAUD_NETWORK: {
                path: 'fraud-network',
                element: <SearchClientPage />,
                featurePermission: [FeatureKeys.SearchFraudNetwork],
            },
            AUTHORIZED_DEVICES: {
                path: 'authorized-devices',
                element: <SearchClientPage />,
                mixpanelName: MixpanelEvents.ViewClientSearchAuthorizedDevices,
                featurePermission: [FeatureKeys.SearchAuthorizedDevices],
            },
            TIMELINE: {
                path: 'timeline',
                element: <SearchClientPage />,
                mixpanelName: MixpanelEvents.ViewClientSearchTimeline,
                featurePermission: [FeatureKeys.SearchTimelineTab],
            },
        },
        PHONE_SEARCH: {
            path: SEARCH_PHONE_PATH + '/:phoneNumber',
            indexRoute: <PhoneSearchPage />,
            mixpanelName: MixpanelEvents.ViewPhoneSearch,
            TIMELINE: {
                path: 'timeline',
                element: <PhoneSearchPage />,
                mixpanelName: MixpanelEvents.ViewPhoneSearchTimeline,
                featurePermission: [FeatureKeys.SearchTimelineTab],
            },
            VOICE_CLUSTER: {
                path: 'voice-cluster',
                element: <PhoneSearchPage />,
                featurePermission: [FeatureKeys.SearchVoiceMap],
            },
            FRAUD_NETWORK: {
                path: 'fraud-network',
                element: <PhoneSearchPage />,
                featurePermission: [FeatureKeys.SearchFraudNetwork],
            },
            AUTHORIZED_DEVICES: {
                path: 'authorized-devices',
                element: <PhoneSearchPage />,
                mixpanelName: MixpanelEvents.ViewPhoneSearchAuthorizedDevices,
                featurePermission: [FeatureKeys.SearchAuthorizedDevices],
            },
            PHONE_HISTORY: {
                path: 'phone-history',
                element: <PhoneSearchPage />,
            },
        },
        GROUPED_BY_EXTERNAL_ID: {
            path: 'grouped-by-external-id',
            element: <GroupedByExternalIdPage />,
            showOnSidebar: false,
            mixpanelName: MixpanelEvents.SearchGroupedByExternalId,
        },
    },
    FLAGS: {
        icon: 'i-ph-warning-bold',
        path: '/flags',
        indexRoute: <FlagsPage />,
        showOnSidebar: true,
        mixpanelName: MixpanelEvents.ViewAlertList,
        featurePermission: [FeatureKeys.Flags],
        DETAILS: {
            path: 'details/:id',
            element: <FlagDetailsPage />,
            mixpanelName: MixpanelEvents.ViewAlertDetails,
            modularProductPermission: [ModularProductTier.FraudShield],
        },
        FINISH_ANALYSIS: {
            path: `${FLAGS_FINISH_ANALYSIS_PATH}/:id`,
            element: <FlagFinishAnalysisPage />,
            mixpanelName: MixpanelEvents.ViewFlagsFinishAnalysis,
            modularProductPermission: [ModularProductTier.FraudShield],
        },
        GROUPED_PHONE: {
            featurePermission: [FeatureKeys.Flags],
            path: 'grouped-phone',
            element: <FlagsGroupedByPhonesPage />,
            showOnSidebar: false,
            mixpanelName: MixpanelEvents.ViewGroupedPhoneAlerts,
            modularProductPermission: [ModularProductTier.FraudShield],
        },
        GROUPED_VOICE: {
            featurePermission: [FeatureKeys.Flags],
            path: 'grouped-voice',
            element: <FlagsGroupedByVoicesPage />,
            showOnSidebar: false,
            mixpanelName: MixpanelEvents.ViewGroupedVoiceAlerts,
            modularProductPermission: [ModularProductTier.FraudShield],
        },
    },
    CASES: {
        icon: 'i-ph-folders-bold',
        path: CASES_PATH,
        indexRoute: <CasesPage />,
        showOnSidebar: true,
        featurePermission: [FeatureKeys.Cases],
        profilePermission: [UserProfiles.Master],
        mixpanelName: MixpanelEvents.ViewCases,
        PHONE: {
            path: 'phone/:id',
            element: <PhoneCasePage />,
            modularProductPermission: [ModularProductTier.FraudShield, ModularProductTier.MindsId],
            profilePermission: [UserProfiles.Master],
        },
        VOICE: {
            path: 'voice/:id',
            element: <VoiceCasePage />,
            modularProductPermission: [ModularProductTier.FraudShield, ModularProductTier.MindsId],
            profilePermission: [UserProfiles.Master],
        },
    },
    CUSTOMERS: {
        icon: 'i-ph-users-three-bold',
        path: CUSTOMERS_PATH,
        indexRoute: <CustomersPage />,
        showOnSidebar: true,
        mixpanelName: MixpanelEvents.ViewCustomers,
        featurePermission: [FeatureKeys.Customers],
        IMPORT: {
            path: 'import/',
            IMPORT_INSERTION: {
                path: 'insertion',
                element: <CertifiedBiometricsImportInsertionPage />,
                mixpanelName:
                    MixpanelEvents.ViewCertifiedBiometricsImportInsert,
            },
            IMPORT_REMOVAL: {
                path: 'removal',
                element: <CertifiedBiometricsImportRemovalPage />,
                mixpanelName:
                    MixpanelEvents.ViewCertifiedBiometricsImportRemoval,
            },
        },
    },
    WATCHLISTS: {
        icon: 'i-ph-user-bold',
        path: '/watchlists',
        indexRoute: <WatchlistsPage />,
        mixpanelName: MixpanelEvents.ViewWatchlists,
        featurePermission: [
            FeatureKeys.WatchlistsPhones,
            FeatureKeys.WatchlistsVoices,
            FeatureKeys.WatchlistsConfig,
        ],
        showOnSidebar: true,
        modularProductPermission: [ModularProductTier.FraudShield],
        DETAILS: {
            path: 'details/:id',
            element: <WatchlistsDetailsPage />,
            modularProductPermission: [ModularProductTier.FraudShield],
        },
        IMPORT_PHONES: {
            path: 'details/:id/import-phones',
            element: <WatchlistImportPhonesPage />,
            modularProductPermission: [ModularProductTier.FraudShield],
        },
    },
    SILENCED_LIST: {
        icon: 'i-ph-speaker-x-bold',
        path: '/silenced-list',
        indexRoute: <SilencedListPage />,
        showOnSidebar: true,
        mixpanelName: MixpanelEvents.ViewSilencedList,
        featurePermission: [FeatureKeys.SilencedList],
        INSERT: {
            path: INSERT_PATH,
            element: <SilencedListInsertPage />,
            mixpanelName: MixpanelEvents.ViewSilencedListInsert,
        },
        IMPORT: {
            path: 'import',
            element: <SilencedListImportPage />,
            mixpanelName: MixpanelEvents.ViewSilencedListImport,
        },
    },
    REPORTS: {
        path: '/reports',
        icon: 'i-ph-chart-line-bold',
        showOnSidebar: true,
        indexRoute: <Navigate replace to="successful-operations" />,
        SUCCESSFUL_OPERATIONS: {
            icon: 'i-ph-users-bold',
            path: 'successful-operations',
            element: <SuccessfulOperationsReportPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewOperationsReport,
            featurePermission: [FeatureKeys.SuccessfulOperationsReport],
        },
        CERTIFIED_BIOMETRICS: {
            icon: 'i-ph-shield-star-bold',
            path: 'certified-biometrics',
            indexRoute: <CertifiedBiometricsReportPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewCertifiedBiometricsReport,
            featurePermission: [FeatureKeys.CertifiedBiometricsReport],
            DETAILS: {
                path: 'details',
                element: <CertifiedBiometricsReportDetailsPage />,
                mixpanelName:
                    MixpanelEvents.ViewCertifiedBiometricsReportDetails,
            },
            IMPORT: {
                path: 'import/',
                IMPORT_INSERTION: {
                    path: 'insertion',
                    element: <CertifiedBiometricsImportInsertionPage />,
                    mixpanelName:
                        MixpanelEvents.ViewCertifiedBiometricsImportInsert,
                },
                IMPORT_REMOVAL: {
                    path: 'removal',
                    element: <CertifiedBiometricsImportRemovalPage />,
                    mixpanelName:
                        MixpanelEvents.ViewCertifiedBiometricsImportRemoval,
                },
            },
        },
        OPERATIONAL_REPORT: {
            icon: 'i-ph-chart-pie-bold',
            path: 'operational-report',
            indexRoute: <OperationalReportPage />,
            mixpanelName: MixpanelEvents.ViewOperationalVisionReport,
            DETAILS: {
                path: 'details/:month',
                element: <OperationalReportDetailsPage />,
                mixpanelName: MixpanelEvents.ViewOperationalVisionReportDetails,
            },
            featurePermission: [FeatureKeys.Pricing],
            showOnSidebar: true,
        },
        REPORT_FLAGS: {
            icon: 'i-ph-warning-bold',
            path: 'report-flags',
            indexRoute: <ReportFlagsPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewFlagsReport,
            featurePermission: [FeatureKeys.ReportFlags],
        },
        WATCHLIST_VALIDATION_REPORT: {
            icon: 'i-ph-user-list-bold',
            path: 'watchlist-validation-report',
            indexRoute: <WatchlistValidationReportPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewWatchlistValidationReport,
            featurePermission: [FeatureKeys.WatchlistsValidationReport],
        },
        ALLOWLIST_VALIDATION_REPORT: {
            icon: 'i-ph-speaker-slash-bold',
            path: 'allowlist-validation-report',
            indexRoute: <AllowlistValidationReportPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewAllowlistValidationReport,
            featurePermission: [FeatureKeys.AllowlistValidationReport],
        },
        CREDIBILITY_REPORT: {
            icon: 'i-ph-chart-bar-horizontal-bold',
            path: 'credibility-report',
            indexRoute: <CredibilityReportPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewCredibilityReport,
            featurePermission: [FeatureKeys.CredibilityReport],
        },
        PHONE_REPORT: {
            icon: 'i-ph-phone-bold',
            path: 'phone-report',
            indexRoute: <PhoneReportPage />,
            showOnSidebar: true,
            featurePermission: [FeatureKeys.PhoneReport],
            modularProductPermission: [ModularProductTier.FraudShield],
            PHONES_LIST: {
                path: 'phones',
                element: <PhoneReportPhonesListPage />,
                showOnSidebar: false,
                modularProductPermission: [ModularProductTier.FraudShield],
            },
        },
        CERTIFIED_AUTHENTICATIONS: {
            icon: 'i-ph-shield-check-bold',
            path: 'certified-authentications',
            indexRoute: <CertifiedAuthenticationsReportPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewCertifiedAuthenticationsReport,
            featurePermission: [FeatureKeys.CertifiedAuthenticationsReport]
        },
    },
    CONFIG: {
        GENERAL_CONFIGURATION: {
            icon: 'i-ph-globe-bold',
            path: 'general-configuration',
            element: <GeneralPage />,
            featurePermission: [
                FeatureKeys.GeneralConfigCompany,
                FeatureKeys.GeneralConfigBiometrics,
                FeatureKeys.GeneralConfigAntifraud,
                FeatureKeys.GeneralConfigAntispoofing,
                FeatureKeys.GeneralConfigGenesys,
                FeatureKeys.OauthLogin,
                FeatureKeys.CertificationMethod,
            ],
            showOnSidebar: true,
        },
        path: CONFIG_PATH,
        icon: 'i-ph-gear-bold',
        showOnSidebar: true,
        indexRoute: <Navigate replace to="users" />,
        mixpanelName: MixpanelEvents.ViewConfig,
        USERS: {
            icon: 'i-ph-users-bold',
            path: 'users',
            indexRoute: <UsersDetailsPage />,
            showOnSidebar: true,
            featurePermission: [
                FeatureKeys.ManageProfiles,
                FeatureKeys.InviteUsers,
                FeatureKeys.ManageUsers,
            ],
            MANAGE_USERS: {
                icon: 'i-ph-users-bold',
                path: 'manage-users',
                element: <ManageUsersNewPage />,
                featurePermission: [FeatureKeys.ManageUsers],
            },
            INVITE_USERS: {
                icon: 'i-ph-user-plus-bold',
                path: INVITE_PATH,
                element: <InviteUsersNewPage />,
                featurePermission: [FeatureKeys.InviteUsers],
            },
            MANAGE_PROFILES: {
                icon: 'i-ph-user-gear-bold',
                path: 'manage-profiles',
                indexRoute: <ManageProfilesPage />,
                featurePermission: [FeatureKeys.ManageProfiles],
                CREATE: {
                    path: 'create',
                    element: <EditCreateProfileNewPage />,
                },
            },
        },
        DOCS: {
            path: 'docs',
            alternativePath: 'api-docs',
            indexRoute: <ApiDocsPage />,
            icon: 'i-ph-book-open-bold',
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewApiDocs,
            openInNewTab: true,
            externalHref: '/config/docs',
            featurePermission: [FeatureKeys.ApiDocs],
            CATCH_ALL: {
                path: '*',
                element: <ApiDocsPage />,
                showOnSidebar: false,
            },
        },
        COMPANY_WEBHOOKS: {
            icon: 'i-ph-sliders-horizontal-bold',
            path: 'company-webhooks',
            indexRoute: <CompanyWebhooksPage />,
            mixpanelName: MixpanelEvents.ViewWebhooksConfig,
            featurePermission: [FeatureKeys.Webhooks],
            DETAILS: {
                path: 'details/:companyWebhookId',
                element: <CompanyWebhooksDetailsPage />,
                mixpanelName: MixpanelEvents.ViewWebhooksConfigDetails,
            },
            showOnSidebar: true,
        },
        API_TOKENS: {
            icon: 'i-ph-key-bold',
            path: 'api-tokens',
            element: <ApiTokensPage />,
            showOnSidebar: true,
            featurePermission: [FeatureKeys.ApiTokens],
        },
        LOGS: {
            icon: 'i-ph-file-text-bold',
            path: 'logs',
            element: <LogsPage />,
            mixpanelName: MixpanelEvents.ViewLogs,
            featurePermission: [FeatureKeys.Logs],
            showOnSidebar: true,
        },
        DEBUG_LOGS: {
            icon: 'i-ph-file-cloud-bold',
            path: 'debug-genesys',
            element: <DebugLogsPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewLogsDebug,
            featurePermission: [FeatureKeys.DebugGenesys],
        },
        MANAGE_TEMPLATES: {
            icon: 'i-ph-file-bold',
            path: 'manage-templates',
            indexRoute: <ManageTemplatesPage />,
            showOnSidebar: true,
            mixpanelName: MixpanelEvents.ViewAlertTemplates,
            featurePermission: [FeatureKeys.ManageTemplates],
            modularProductPermission: [ModularProductTier.FraudShield],
            CREATE: {
                path: 'create',
                element: <EditCreateTemplatePage />,
                mixpanelName: MixpanelEvents.ViewEditCreateTemplate,
                modularProductPermission: [ModularProductTier.FraudShield],
            },
        },
    },
    EDIT_PROFILE: {
        path: '/edit-profile',
        element: <EditProfilePage />,
        mixpanelName: MixpanelEvents.ViewCustomerDetails,
    },
} satisfies Record<string, RouteDefinition>;

export const ROUTES = {
    LOGIN: {
        path: LOGIN_PATH,
        element: <LoginPage />,
        mixpanelName: MixpanelEvents.ViewLogin,
    },
    COMPANY_LOGIN: {
        path: COMPANY_LOGIN_PATH,
        element: <CompanyLoginPage />,
        mixpanelName: MixpanelEvents.ViewCompanyLogin,
    },
    FORGOT_COMPANY_NAME: {
        path: FORGOT_COMPANY_NAME_PATH,
        element: <ForgotCompanyNamePage />,
        mixpanelName: MixpanelEvents.ViewForgotPassword,
    },
    FORGOT_PASSWORD: {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
        mixpanelName: MixpanelEvents.ViewForgotPassword,
    },
    RESET_PASSWORD: {
        path: '/reset-password',
        element: <ResetPasswordPage />,
        mixpanelName: MixpanelEvents.ViewResetPassword,
    },
    PUBLIC: {
        path: '/',
        indexRoute: <Navigate to="/login" />,
        mixpanelName: MixpanelEvents.ViewPublic,
        CONFIRM_ACCOUNT: {
            path: 'confirm-account',
            element: <ConfirmAccountPage />,
            mixpanelName: MixpanelEvents.ViewConfirmAccount,
        },
    },
    OAUTH_LOGIN: {
        path: '/oauth-login/:companyName',
        element: <OauthLoginPage />,
        mixpanelName: MixpanelEvents.ViewOauthLogin,
    },
    NOT_FOUND: {
        path: NOT_FOUND_PATH,
        element: <NotFoundPage />,
        mixpanelName: MixpanelEvents.ViewNotFound,
    },
    ...PROTECTED_ROUTES,
} satisfies Record<string, RouteDefinition>;

export const getChildRoutes = (
    route: RouteDefinition,
    isMenu?: boolean,
): BaseRouteDefinition[] =>
    Object.entries(route)
        .filter(
            ([key, v]) =>
                /^[A-Z]/.test(key) &&
                (!isMenu || (v as BaseRouteDefinition).showOnSidebar),
        )
        .map(([, v]) => v as BaseRouteDefinition);

export const createRoute = (
    definition: BaseRouteDefinition,
    depth = 0,
    parentPath = '',
): RouteObject => {
    const children = getChildRoutes(definition)?.map((c) =>
        createRoute(c, depth + 1, definition.path),
    );

    if (definition.indexRoute) {
        children.push({ index: true, element: definition.indexRoute });
    }

    let routePath = definition.path;
    if (routePath && routePath.includes('/*')) {
        routePath = '*';
    }

    return {
        element: (
            <ProtectedRoute
                depth={depth}
                definition={definition}
                parentPath={parentPath}
                key={(definition.mixpanelName ?? '') + (definition.path ?? '')}
            />
        ),
        path: routePath,
        children,
    };
};

export function getDefinitionFromPath(path: string) {
    let route: RouteDefinition = ROUTES;
    const pathNormalized = path.replace('api-docs', 'docs');
    const normalizedPath = pathNormalized
        .toUpperCase()
        .replaceAll('/', ' ')
        .trim();
    const parts = normalizedPath.split(' ').filter(Boolean);

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const normalizedPart = part?.replace('-', '_') as keyof RouteDefinition;

        if (route.path?.toString().includes(':') && !(normalizedPart in route))
            continue;

        if (!(normalizedPart in route)) {
            if ('CATCH_ALL' in route) {
                const catchAllRoute = route.CATCH_ALL as BaseRouteDefinition;
                const catchAllPath = catchAllRoute.path;
                if (
                    catchAllPath &&
                    (catchAllPath === '*' || catchAllPath.includes('/*'))
                ) {
                    return catchAllRoute;
                }
            }
            break;
        }

        route = route[normalizedPart] as RouteDefinition;
    }

    return route as BaseRouteDefinition;
}
